import { ArrowUpRight } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLines } from "./Reveal";
import SpendCalculator from "./SpendCalculator";

const TIERS = [
  {
    id: 2,
    num: "01",
    name: "Website",
    tagline: "A website that converts.",
    cadence: "Quoted per project",
    rates: [{ amount: "Quoted", suffix: "on your call" }],
    features: [
      "Professional, modern design",
      "Mobile-friendly across every device",
      "Multi-page layout tailored to your business",
      "Contact & lead forms included",
      "Clear, conversion-focused calls-to-action",
      "Basic SEO setup",
      "Launch & setup included",
      "Optional ongoing management plan",
    ],
    featured: false,
    cta: "Request a quote",
  },
  {
    id: 1,
    num: "02",
    name: "Ad Launch",
    tagline: "Meta Ads, done properly.",
    cadence: "Monthly",
    rates: [
      { amount: "$550", suffix: "AUD — Setup (one-time)" },
      { amount: "10%", suffix: "of what you earn — jobs won through the ads" },
    ],
    note: "You choose the ad spend — paid directly to Meta",
    groups: [
      {
        label: "Included in setup",
        items: [
          "Meta Business & Ad Account setup",
          "Meta Pixel & conversion tracking",
          "Audience research",
          "Campaign structure",
          "Creative & copy setup",
          "Facebook & Instagram campaign launch",
        ],
      },
      {
        label: "What the 10% covers",
        items: [
          "Campaign management & optimisation",
          "Audience targeting",
          "Ad copywriting",
          "Creative & ad testing",
          "Budget optimisation",
          "Monthly performance reporting",
          "Ongoing strategy adjustments",
        ],
      },
    ],
    featured: false,
  },
  {
    id: 3,
    num: "03",
    name: "Growth Package",
    tagline: "Ads, website & direction.",
    cadence: "Monthly + one-time",
    rates: [
      { amount: "Quoted", suffix: "website · on your call" },
      { amount: "$450", suffix: "AUD — setup (one-time)" },
      { amount: "10%", suffix: "of what you earn — jobs won through the ads" },
    ],
    note: "Save $100 on setup with the bundle",
    note2: "Ad spend paid directly to Meta.",
    features: [
      "Premium website — design, build & launch",
      "Mobile-friendly, lead-ready design",
      "Meta Pixel, tracking & campaign setup",
      "Facebook & Instagram campaign management",
      "Ad copywriting, creative & ad testing",
      "Budget optimisation & monthly reporting",
      "Positioning & creative direction",
      "Ongoing strategy adjustments",
    ],
    featured: true,
    cta: "Start with the Growth Package",
    ctaNote: "Website quoted on call — discounted setup, then just 10% on jobs won through the ads.",
  },
];

function Bubble({ t, index }) {
  return (
    <FadeUp delay={index * 0.12} className="h-full">
      <div
        data-testid={`pricing-card-${t.id}`}
        className={`relative flex h-full flex-col overflow-hidden rounded-t-[6rem] rounded-b-[2rem] border p-6 pt-12 text-center transition-[border-color,box-shadow,transform] duration-500 sm:p-8 sm:pt-14 ${
          t.featured
            ? "border-[#D4AF37]/50 bg-[#12100a] shadow-[0_0_60px_rgba(212,175,55,0.12)] lg:-translate-y-4"
            : "border-white/10 bg-[#0F0F12] hover:-translate-y-2 hover:border-[#D4AF37]/30 hover:shadow-[0_0_40px_rgba(212,175,55,0.08)]"
        }`}
      >
        {t.featured && (
          <span
            data-testid="pricing-most-popular"
            className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-[#D4AF37] px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-black shadow-[0_0_30px_rgba(212,175,55,0.4)]"
          >
            Most popular
          </span>
        )}
        {t.featured && (
          <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_70%)]" />
        )}
        <p
          className={`font-mono text-[10px] uppercase tracking-[0.3em] ${
            t.featured ? "text-[#E5C158]" : "text-neutral-500"
          }`}
        >
          {t.num} — {t.name}
        </p>
        {t.cadence && (
          <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">
            {t.cadence}
          </p>
        )}
        <h3 className="mt-5 font-display text-2xl font-light tracking-tight sm:text-3xl">
          {t.tagline}
        </h3>
        <div className="my-8 space-y-4 sm:my-10">
          {t.rates.map((r, i) => (
            <p
              key={i}
              data-testid={`pricing-rate-${t.id}${i > 0 ? `-${i}` : ""}`}
              className="font-mono text-3xl font-light tracking-tighter sm:text-4xl"
            >
              {r.amount}
              {r.was && (
                <span className="ml-2 align-middle font-mono text-xs text-neutral-500 line-through">
                  {r.was}
                </span>
              )}
              {r.suffix && (
                <span className="ml-2 align-middle font-mono text-xs uppercase tracking-[0.2em] text-neutral-400">
                  {r.suffix}
                </span>
              )}
            </p>
          ))}
          {t.startRates && (
            <div className="space-y-1.5 pt-1">
              {t.startRates.map((r, i) => (
                <p key={i} className="font-mono text-xs text-neutral-400 sm:text-sm">
                  {r.amount}
                  {r.was && (
                    <span className="ml-2 font-mono text-[10px] text-neutral-500 line-through">{r.was}</span>
                  )}
                  <span className="ml-2 uppercase tracking-[0.15em]">{r.suffix}</span>
                </p>
              ))}
              <p className="pt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5C158]">
                $648 total to start
              </p>
            </div>
          )}
          <div>
          {t.note && (
            <span className="mt-2 inline-block rounded-full border border-[#D4AF37]/30 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#E5C158]/90">
              {t.note}
            </span>
          )}
            {t.free && (
              <p
                data-testid={`pricing-free-${t.id}`}
                className="mt-3 inline-block rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#E5C158]"
              >
                First month of management — FREE
              </p>
            )}
            {t.note2 && (
              <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                {t.note2}
              </p>
            )}
          </div>
        </div>
        {t.groups ? (
          t.groups.map((g, gi) => (
            <div key={g.label} className={`mx-auto max-w-[260px] text-left ${gi > 0 ? "mt-8" : ""}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5C158]">
                {g.label}
              </p>
              <ul className="mt-3 space-y-2.5">
                {g.items.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-neutral-400">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#D4AF37]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))
        ) : (
          <ul className="mx-auto max-w-[240px] space-y-4 text-left">
            {t.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-neutral-400">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#D4AF37]" />
                {f}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-10">
          <a
            data-testid={`pricing-cta-${t.id}`}
            href={`mailto:CFDigiCo@gmail.com?subject=${encodeURIComponent(`${t.cta ? t.cta : "Rate inquiry"} — ${t.name}`)}`}
            className={`group flex items-center justify-center gap-2 rounded-full px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-[background-color,color,border-color,box-shadow] duration-300 ${
              t.featured
                ? "bg-[#D4AF37] text-black hover:bg-[#E5C158] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
                : "border border-white/15 text-white hover:border-[#D4AF37]/60 hover:text-[#E5C158]"
            }`}
          >
            {t.cta || "Request a rate"}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          {t.ctaNote && (
            <p className="mx-auto mt-4 max-w-[260px] font-mono text-[9px] uppercase leading-relaxed tracking-[0.15em] text-neutral-500">
              {t.ctaNote}
            </p>
          )}
        </div>
      </div>
    </FadeUp>
  );
}

export default function Pricing() {
  return (
    <section id="pricing" className="relative border-t border-white/[0.06] py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Eyebrow className="mb-8">03 — Pricing</Eyebrow>
            <MaskedLines
              inView
              as="h2"
              className="font-display text-3xl font-light leading-tight tracking-tight sm:text-5xl"
              lines={[
                { content: "Simple tiers." },
                { content: <span className="gold-text italic">Straight pricing.</span> },
              ]}
            />
          </div>
          <FadeUp delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-neutral-400">
              Final rates are confirmed on your discovery call — every
              engagement is scoped to your goals, never to a template.
            </p>
          </FadeUp>
        </div>

        <div className="mx-auto mt-16 grid max-w-5xl gap-8 sm:gap-10 md:grid-cols-3 md:gap-6 lg:gap-8" data-testid="pricing-grid">
          {TIERS.map((t, i) => (
            <Bubble key={t.id} t={t} index={i} />
          ))}
        </div>

        <SpendCalculator />

        <FadeUp delay={0.2} className="mx-auto mt-12 max-w-5xl">
          <div data-testid="service-fee-note" className="rounded-[2rem] border border-white/10 bg-[#0F0F12] p-7 sm:p-9">
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#E5C158]">
              <span className="h-px w-8 bg-[#D4AF37]/60 shrink-0" />
              How it works — 10% only
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-neutral-400">
              No monthly fees, ever. You pay the $550 AUD setup once, then 10%
              of what you earn from jobs won through the ads — a $7k job pays me
              $700. Only ad-won jobs count, nothing else. No jobs from the ads,
              no payment. Ad spend is separate and paid directly to Meta by the
              client.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}